import { useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "@follosoft/react-components";

import type { ContractContactAlertType } from "../types";

import { useContractContactConstants } from "../hooks/use-contract-contact-constants";

import { CONTRACT_CONTACTS_ENDPOINT, RESOURCE_TYPE } from "./endpoints";
import { contractContactsQueryKey } from "./query-keys";

export interface ToggleAlertInput {
  contactId: string;
  alertType: ContractContactAlertType;
  /** The channel to alert, named by its value. The resource resolves it against
   * that contact's channels of the matching kind, so the row lands on the
   * number or address the box sits next to rather than on whichever one the
   * server would have picked. */
  destination: string;
  /** The subscription behind the box when it is ticked; `null` when it isn't. */
  contractContactId: string | null;
}

/**
 * Turns one alert box on or off. Ticking stores a subscription, unticking
 * destroys it — the row is the subscription, so there is no "off" state to
 * keep.
 *
 * `status_id` is left out so the backend applies its `not_sent` default: the
 * opt-in is dispatched by its own flow, never by stamping a row here.
 */
export const useToggleAlertService = (contractId: string | number) => {
  const queryClient = useQueryClient();
  const { requireAlertTypeId } = useContractContactConstants();

  return useMutation({
    mutationFn: async ({ contactId, alertType, destination, contractContactId }: ToggleAlertInput) => {
      if (contractContactId) {
        await axiosInstance().delete(`${CONTRACT_CONTACTS_ENDPOINT}/${contractContactId}`);

        return;
      }

      // Both relationships are required, and `contact` has to carry a reachable
      // id — the resolver reports a missing one as a bare `contact_id`, not
      // under its mapped path.
      await axiosInstance().post(CONTRACT_CONTACTS_ENDPOINT, {
        data: {
          type: RESOURCE_TYPE,
          attributes: { alert_type_id: requireAlertTypeId(alertType), destination },
          relationships: {
            contract: { data: { type: "contracts", id: String(contractId) } },
            contact: { data: { type: "contacts", id: String(contactId) } },
          },
        },
      });
    },
    // The server has the last word: it recomputes more than was sent
    // (`will_receive_alerts` and the blocker reason).
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: contractContactsQueryKey(String(contractId)) });
    },
  });
};
